// [PURPOSE]: Pure helper that PLANS what a drinkPotion call should do — given an
//   item with effects[] + a rollDice function, produces a declarative plan describing:
//     - immediate state mutations (healing / damage HP writes)
//     - temp HP gains (one per tempHP effect)
//     - applied QB entries (timed effects: tempHP / spell-grant / spell-cast / ability / resistance / speed / special)
//     - per-effect log + status messages (so orchestrator can fire them in order)
//   It does NOT mutate state, register into effectRegistry, or emit systemBus events —
//   the orchestrator (drinkPotion in App.jsx) does that after consuming the plan.
//
// [WHY EXTRACTED — Sub-plan 1U-impl-E, 2026-05-27]:
//   Pre-refactor drinkPotion early-returned on the first applied effect via `return;`
//   inside the effects loop. Multi-effect potions silently dropped trailing effects:
//     - Potion of Heroism [tempHP, spell:Bless grant]: tempHP early-return → Bless never registered
//     - Potion of Poison  [damage, condition]:         damage early-return → condition never reached
//   The bug was control-flow shape, not per-effect logic. Extracting an accumulator
//   pattern into a pure planner makes the loop testable WITHOUT DOM-driving App.jsx
//   (Rule 22 — extract → import → assert; Rule 31(f) — producer→register→reader).
//
// [SHAPE CONTRACT]:
//   planPotionEffects(item, { rollDice }) returns:
//     {
//       appliedEffects: Array<{ ...potionBuilt, id, startTime }>,       // QB entries to append
//       immediateActions: Array<{ type, formula, roll, damageType? }>,  // healing/damage HP writes
//       tempHpGains: Array<{ value, source }>,                          // gainTempHP() calls
//       logEntries: Array<{ kind, message, category, details }>,        // per-effect activity log
//       statusEntries: Array<{ message, duration, options }>,           // per-effect showStatus
//       systemBusEntries: Array<{ event, payload }>,                    // QUICK_BONUS_ADDED for catch-all
//       didApplyAnything: boolean,                                       // orchestrator gates consumePotion()
//       processedEffectCount: number,                                    // loop iteration audit (Rule 31(f) test contract)
//       pendingCastModal: Array<{ spellName, saveAbility, dc, itemName }> | null,
//                                                                        // non-null only for operation:'cast' with dc!=null
//                                                                        // (Animal Friendship DC13 WIS, Detect Thoughts DC13 INT)
//                                                                        // App.jsx orchestrator opens UsableAbilityModal for each.
//                                                                        // No-save cast potions (Growth, Diminution,
//                                                                        // Clairvoyance, Gaseous Form) are NOT included.
//     }
//
// [CONTRACT NOTE — preserved from L4080-4136 pre-refactor]:
//   - Healing branch reads canonical currentHitPoints + writes both currentHitPoints + hp (Rule 1V).
//   - Damage branch same as above with subtract instead of add.
//   - tempHP branch calls gainTempHP() (PHB p.198 non-stacking rule lives inside gainTempHP).
//   - Spell grant/cast: appends QB with type=spellName.toLowerCase() so b.type==='bless'/'haste'
//     hardcoded handlers still fire (1Q-F-5b spec; retirement queued as 1Q-F-5b-ii).
//   - Catch-all timed (ability/resistance/speed/special): appends QB, emits QUICK_BONUS_ADDED.
//   - Unknown types (e.g., condition — Sub-plan 1U-impl-H, NEW): silently skipped (potionBuilt === null).
//
// [ID UNIQUENESS]:
//   Pre-refactor used Date.now(); within a single drinkPotion call multiple QB entries
//   would collide (same ms). Use a baseTime + per-effect index offset for uniqueness within
//   a planning batch — every entry gets a distinct id within the same call.
//
// [CONSUMERS]: src/App.jsx drinkPotion handler.
// [TESTS]: src/__tests__/sub-plan-1U-impl-E-drinkpotion-multieffect-loop.test.js

import { buildAppliedPotionEffect } from './potionEffectApply';

/**
 * Plan the side-effects of drinking a potion with multi-effect support.
 *
 * @param {Object} item                     - Inventory item (needs .name and .effects[])
 * @param {Object} options
 * @param {Function} options.rollDice       - Dice rolling function (matching App.jsx rollDice shape:
 *                                            takes a notation string, returns { total, text, ... })
 * @returns {Object} plan describing what the orchestrator should do (see SHAPE CONTRACT above)
 */
export function planPotionEffects(item, { rollDice }) {
  const effects = (item && item.effects) || [];
  const appliedEffects = [];
  const immediateActions = [];
  const tempHpGains = [];
  const logEntries = [];
  const statusEntries = [];
  const systemBusEntries = [];
  // [Sub-plan A1-cast-potions 2026-05-30]: cast effects with a save DC queue a modal entry.
  // Only operation:'cast' + effect.dc != null potions get an entry here.
  // The orchestrator (drinkPotion in App.jsx) opens UsableAbilityModal for each.
  const pendingCastModal = [];
  let processedEffectCount = 0;

  // Use a single baseTime per call so all generated ids share the same window
  // but each entry is unique within that call (baseTime + index).
  const baseTime = Date.now();

  for (let i = 0; i < effects.length; i++) {
    const effect = effects[i];
    processedEffectCount++;

    // ----- Immediate: healing -----
    if (effect.type === 'healing') {
      const healFormula = effect.value || '2d4+2';
      const roll = rollDice(healFormula);
      immediateActions.push({
        kind: 'healing',
        formula: healFormula,
        roll
      });
      statusEntries.push({
        message: `Healed for ${roll.total} HP! (${roll.text})`,
        duration: 5000,
        options: {
          breakdown: `Rolled ${healFormula}`,
          rolls: roll.text,
          total: roll.total
        }
      });
      logEntries.push({
        kind: 'healing',
        message: `Drank ${item.name}`,
        category: 'items',
        details: { healing: roll.total, roll: roll.text }
      });
      continue;
    }

    // ----- Immediate: damage (poison etc.) -----
    if (effect.type === 'damage') {
      const damageFormula = effect.value || '3d6';
      const roll = rollDice(damageFormula);
      immediateActions.push({
        kind: 'damage',
        formula: damageFormula,
        roll,
        damageType: effect.damageType
      });
      statusEntries.push({
        message: `Took ${roll.total} ${effect.damageType || ''} damage! (${roll.text})`,
        duration: 5000,
        options: {
          breakdown: `Rolled ${damageFormula} damage`,
          rolls: roll.text,
          total: roll.total
        }
      });
      logEntries.push({
        kind: 'damage',
        message: `Drank ${item.name}`,
        category: 'combat',
        details: {
          damage: roll.total,
          damageType: effect.damageType,
          roll: roll.text
        }
      });
      continue;
    }

    // ----- Delegated to buildAppliedPotionEffect (helper) -----
    const potionBuilt = buildAppliedPotionEffect(item, effect);
    if (!potionBuilt) {
      // Unknown / unhandled effect type (e.g., 'condition' — pre-1U-impl-H).
      // Silently drop — caller-visible miss is by design until a handler exists.
      continue;
    }

    // Unique id within this drinkPotion call (baseTime + index ensures no collisions
    // even when multiple QB entries are pushed in the same ms).
    const idBase = baseTime + i;

    // ----- tempHP branch -----
    if (potionBuilt.type === 'tempHP') {
      tempHpGains.push({ value: potionBuilt.value, source: item.name });
      appliedEffects.push({
        ...potionBuilt,
        id: idBase,
        startTime: baseTime
      });
      // tempHP path uses gainTempHP's own log/status — no per-effect log entry here.
      continue;
    }

    // ----- Spell grant/cast branch -----
    // [Sub-plan 1U-impl-H 2026-05-28]: type guard required. Condition QB entries carry
    // `operation: 'grant'` but are NOT spells. Without this guard, the inner
    // `potionBuilt.spellName !== undefined` check fails and the outer `continue` silently
    // drops the condition entry before it reaches the catch-all. The type guard ensures
    // condition entries always bypass this branch and land in the catch-all below.
    // [Sub-plan 1H-G Stage 2 2026-05-29]: Extend type guard to also exclude 'feature' — the
    // waterBreathing feature QB entry carries operation:'grant' (mirroring the SRD effect
    // shape) but has no spellName, so without this guard it silently hits the `continue` at
    // the inner else below and never reaches the catch-all. 'feature' is not a spell type.
    if (potionBuilt.type !== 'condition' && potionBuilt.type !== 'feature' && (potionBuilt.operation === 'grant' || potionBuilt.operation === 'cast')) {
      if (potionBuilt.spellName !== undefined) {
        appliedEffects.push({
          ...potionBuilt,
          id: idBase,
          startTime: baseTime
        });
        if (potionBuilt.operation === 'grant') {
          logEntries.push({
            kind: 'spell-grant',
            message: `Drank ${item.name} — granted ${potionBuilt.spellName} for ${potionBuilt.duration}`,
            category: 'potion',
            details: null
          });
          statusEntries.push({
            message: `✨ ${potionBuilt.spellName} active (${potionBuilt.duration})`,
            duration: 3000,
            options: null
          });
        } else {
          // cast — [Sub-plan A1-cast-potions 2026-05-30]:
          // If the effect carries a save DC, queue a UsableAbilityModal entry so the
          // orchestrator can prompt the player for the save result. The modal onResolve
          // handler logs the outcome (success/failure). No automatic log entry here.
          //
          // If there is NO dc (Enlarge, Reduce, Clairvoyance, Gaseous Form), keep the
          // existing QB+log path — a modal with no rolls and no save would be empty.
          if (potionBuilt.dc != null) {
            // Data-driven: read saveAbility from the effect field in gameData.js.
            // potion_animal_friendship → saveAbility:'wisdom'
            // potion_mind_reading      → saveAbility:'intelligence'
            // For any future DC-bearing cast potion, add saveAbility to its effect in gameData.js.
            const saveAbility = potionBuilt.saveAbility || 'wisdom';
            pendingCastModal.push({
              spellName: potionBuilt.spellName,
              saveAbility,
              dc: potionBuilt.dc,
              itemName: item.name
            });
            // Minimal status to acknowledge the cast immediately; the modal handles the log.
            statusEntries.push({
              message: `✨ Cast ${potionBuilt.spellName} — DC ${potionBuilt.dc} save required`,
              duration: 3000,
              options: null
            });
          } else {
            // No-save cast: keep existing QB + log (no modal needed).
            logEntries.push({
              kind: 'spell-cast',
              message: `Drank ${item.name} — cast ${potionBuilt.spellName}`,
              category: 'potion',
              details: null
            });
            statusEntries.push({
              message: `✨ Cast ${potionBuilt.spellName}`,
              duration: 3000,
              options: null
            });
          }
        }
        continue;
      }
      // operation set but spellName missing — fall through to catch-all? No;
      // matches pre-refactor behavior which left the inner if guard but did not
      // advance to catch-all. Skip silently.
      continue;
    }

    // ----- Catch-all timed effect (ability set, resistance, speed, special) -----
    appliedEffects.push({
      ...potionBuilt,
      id: idBase,
      startTime: baseTime
    });
    // The catch-all path is the only one that emits QUICK_BONUS_ADDED + per-effect
    // log/status in the pre-refactor code (L4225-4235). Preserve that.
    systemBusEntries.push({
      payload: {
        bonusId: potionBuilt.name || item.name,
        bonusName: potionBuilt.name || item.name,
        bonusType: potionBuilt.affects,
        value: potionBuilt.bonus,
        duration: potionBuilt.duration,
        source: 'potion'
      }
    });
    statusEntries.push({
      message: `Drank ${item.name} - ${potionBuilt.affects} ${potionBuilt.bonus}`,
      duration: 5000,
      options: null
    });
    logEntries.push({
      kind: 'catchall',
      message: `Drank ${item.name}`,
      category: 'items',
      details: { effect: { ...potionBuilt, id: idBase, startTime: baseTime } }
    });
  }

  const didApplyAnything =
    immediateActions.length > 0 ||
    tempHpGains.length > 0 ||
    appliedEffects.length > 0 ||
    pendingCastModal.length > 0;

  return {
    appliedEffects,
    immediateActions,
    tempHpGains,
    logEntries,
    statusEntries,
    systemBusEntries,
    didApplyAnything,
    processedEffectCount,
    pendingCastModal: pendingCastModal.length > 0 ? pendingCastModal : null
  };
}

/**
 * Pure helper: builds the activity-log message for a cast-potion save resolution.
 * Extracted so that handleCastPotionResolve in App.jsx and the A1 unit test share
 * the SAME implementation (Rule 22 — no inline test replicas).
 *
 * @param {object} resolution - { saveOutcome: 'success'|'failure', target?: string }
 * @param {object} modalData  - { spellName, dc, itemName }
 * @returns {string}
 */
export function buildCastPotionResolveMessage(resolution, modalData) {
  const { spellName, dc, itemName } = modalData;
  const targetName = resolution.target || 'target';
  const outcome = resolution.saveOutcome;
  const resultText = outcome === 'success'
    ? `${targetName} resisted (save succeeded)`
    : `${targetName} affected (save failed)`;
  return `Cast ${spellName} from ${itemName} — DC ${dc} save: ${resultText}`;
}
